import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  XCircle,
} from "lucide-react";
import { createClientId } from "../../lib/identity.js";

const TOAST_META = {
  success: {
    icon: CheckCircle2,
    label: "成功",
    role: "status",
  },
  error: {
    icon: XCircle,
    label: "错误",
    role: "alert",
  },
  warning: {
    icon: AlertTriangle,
    label: "警告",
    role: "alert",
  },
  info: {
    icon: Info,
    label: "提示",
    role: "status",
  },
};

export function useToastQueue(limit = 4) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((toastId) => {
    setToasts((current) => current.filter((toast) => toast.id !== toastId));
  }, []);

  const pushToast = useCallback((toast) => {
    const nextToast = {
      id: createClientId(),
      type: "info",
      duration: 3600,
      ...toast,
    };
    setToasts((current) => [...current, nextToast].slice(-limit));
    return nextToast.id;
  }, [limit]);

  return {
    dismissToast,
    pushToast,
    toasts,
  };
}

export function ToastStack({ toasts = [], onDismiss, position = "top-right" }) {
  if (!toasts.length) {
    return null;
  }

  return (
    <div className="frontend-toast-stack" data-position={position} aria-label="通知">
      {toasts.map((toast) => (
        <ToastItem toast={toast} onDismiss={onDismiss} key={toast.id} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const type = TOAST_META[toast.type] ? toast.type : "info";
  const meta = TOAST_META[type];
  const Icon = meta.icon;

  useEffect(() => {
    if (toast.duration === 0 || toast.duration === null) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      onDismiss(toast.id);
    }, Number(toast.duration || 3600));
    return () => window.clearTimeout(timer);
  }, [onDismiss, toast.duration, toast.id]);

  return (
    <section className="frontend-toast" data-type={type} role={meta.role} aria-live={meta.role === "alert" ? "assertive" : "polite"}>
      <span className="frontend-toast-icon" aria-hidden="true">
        <Icon size={18} />
      </span>
      <span className="frontend-toast-body">
        <strong>{toast.title || meta.label}</strong>
        {toast.message ? <span>{toast.message}</span> : null}
      </span>
      <button type="button" aria-label="关闭通知" onClick={() => onDismiss(toast.id)}>
        <X size={16} aria-hidden="true" />
      </button>
    </section>
  );
}
